import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { PenLine, Send, Copy, Check, RotateCcw, Clock, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/writing`;

const T = {
  nl: {
    title: 'Schrijfassistent',
    subtitle: 'Herschrijf, verbeter of genereer teksten met AI',
    placeholder: 'Plak of typ hier je tekst...',
    rewrite: 'Herschrijven',
    reply: 'Antwoord genereren',
    improve: 'Verbeteren',
    tone: 'Toon',
    result: 'Resultaat',
    copied: 'Gekopieerd!',
    copy: 'Kopieer',
    history: 'Geschiedenis',
    noHistory: 'Nog geen schrijfhistorie — maak iets om te beginnen.',
    contextPlaceholder: 'Extra context (optioneel)',
    tones: { professional: 'Professioneel', friendly: 'Vriendelijk', concise: 'Beknopt', formal: 'Formeel', casual: 'Informeel' },
  },
  en: {
    title: 'Writing Assistant',
    subtitle: 'Rewrite, improve or generate texts with AI',
    placeholder: 'Paste or type your text here...',
    rewrite: 'Rewrite',
    reply: 'Generate Reply',
    improve: 'Improve',
    tone: 'Tone',
    result: 'Result',
    copied: 'Copied!',
    copy: 'Copy',
    history: 'History',
    noHistory: 'No writing history yet — create something to get started.',
    contextPlaceholder: 'Extra context (optional)',
    tones: { professional: 'Professional', friendly: 'Friendly', concise: 'Concise', formal: 'Formal', casual: 'Casual' },
  },
};

export default function WritingAssistantPage() {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = T[lang] || T.nl;
  const headers = { Authorization: `Bearer ${token}` };

  const [text, setText] = useState('');
  const [context, setContext] = useState('');
  const [tone, setTone] = useState('professional');
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const resultRef = useRef(null);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/history?limit=10`, { headers });
      setHistory(res.data.history || []);
    } catch {}
  }, [token]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleAction = async (action) => {
    if (!((text) == null ? '' : String(text)).trim()) return;
    setLoading(true);
    setResult('');
    try {
      let res;
      if (action === 'rewrite') {
        res = await axios.post(`${API}/rewrite`, { text, tone, language: lang, context: context || undefined }, { headers });
        setResult(res.data.rewritten);
      } else if (action === 'reply') {
        res = await axios.post(`${API}/reply`, { original_message: text, tone, language: lang, context: context || undefined }, { headers });
        setResult(res.data.reply);
      } else if (action === 'improve') {
        res = await axios.post(`${API}/improve`, { text, language: lang }, { headers });
        setResult(res.data.improved);
      }
      fetchHistory();
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth' }), 200);
    } catch (err) {
      setResult(err.response?.data?.detail || 'Er ging iets mis');
    }
    setLoading(false);
  };

  const copyResult = () => {
    navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="page-container" data-testid="writing-assistant-page">
      <div className="page-header">
        <div className="page-header-icon"><PenLine size={24} /></div>
        <div>
          <h1 className="page-title">{t.title}</h1>
          <p className="page-subtitle">{t.subtitle}</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1.5rem' }}>
        {/* Input Panel */}
        <div className="card" style={{ padding: '1.5rem' }}>
          <textarea
            data-testid="writing-input"
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={t.placeholder}
            rows={8}
            style={{
              width: '100%', background: 'var(--bg-input)', border: '1px solid var(--border)',
              borderRadius: '0.5rem', padding: '0.75rem', color: 'var(--text-primary)',
              fontSize: '0.88rem', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6,
            }}
          />
          <input
            data-testid="writing-context"
            value={context}
            onChange={e => setContext(e.target.value)}
            placeholder={t.contextPlaceholder}
            style={{
              width: '100%', background: 'var(--bg-input)', border: '1px solid var(--border)',
              borderRadius: '0.5rem', padding: '0.5rem 0.75rem', color: 'var(--text-primary)',
              fontSize: '0.82rem', marginTop: '0.75rem',
            }}
          />

          {/* Tone selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600 }}>{t.tone}:</span>
            {Object.entries(t.tones).map(([key, label]) => (
              <button
                key={key}
                data-testid={`tone-${key}`}
                onClick={() => setTone(key)}
                style={{
                  padding: '4px 12px', borderRadius: '1rem', fontSize: '0.75rem', fontWeight: 500,
                  border: `1px solid ${tone === key ? 'var(--accent)' : 'var(--border)'}`,
                  background: tone === key ? 'rgba(99,102,241,0.15)' : 'transparent',
                  color: tone === key ? 'var(--accent)' : 'var(--text-muted)',
                  cursor: 'pointer', transition: 'all 0.15s',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <button data-testid="btn-rewrite" onClick={() => handleAction('rewrite')} disabled={loading || !((text) == null ? '' : String(text)).trim()} className="btn-primary" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <RotateCcw size={14} /> {t.rewrite}
            </button>
            <button data-testid="btn-reply" onClick={() => handleAction('reply')} disabled={loading || !((text) == null ? '' : String(text)).trim()} className="btn-primary" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}>
              <Send size={14} /> {t.reply}
            </button>
            <button data-testid="btn-improve" onClick={() => handleAction('improve')} disabled={loading || !((text) == null ? '' : String(text)).trim()} className="btn-primary" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}>
              <PenLine size={14} /> {t.improve}
            </button>
          </div>
        </div>

        {/* Result Panel */}
        <div className="card" style={{ padding: '1.5rem' }} ref={resultRef}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>{t.result}</span>
            {result && (
              <button data-testid="copy-result" onClick={copyResult} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: copied ? 'var(--success)' : 'var(--text-muted)', cursor: 'pointer', fontSize: '0.78rem' }}>
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? t.copied : t.copy}
              </button>
            )}
          </div>

          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, gap: 8 }}>
              <div className="spinner" style={{ width: 20, height: 20, border: '2px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Emma schrijft...</span>
            </div>
          ) : result ? (
            <div style={{ background: 'var(--bg-input)', borderRadius: '0.5rem', padding: '1rem', color: 'var(--text-primary)', fontSize: '0.88rem', lineHeight: 1.6, whiteSpace: 'pre-wrap', minHeight: 150, border: '1px solid var(--border)' }}>
              {result}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              {lang === 'nl' ? 'Het resultaat verschijnt hier...' : 'The result will appear here...'}
            </div>
          )}
        </div>
      </div>

      {/* History */}
      <div className="card" style={{ marginTop: '1.5rem', padding: '1rem 1.5rem' }}>
        <button onClick={() => setShowHistory(!showHistory)} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)', fontSize: '0.85rem', fontWeight: 600, width: '100%' }}>
          <Clock size={16} /> {t.history}
          <ChevronDown size={14} style={{ marginLeft: 'auto', transform: showHistory ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        </button>
        {showHistory && (
          <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {history.length === 0 ? (
              <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>{t.noHistory}</span>
            ) : (Array.isArray(history) ? history : []).map((h, i) => (
              <div key={i} style={{ padding: '0.5rem 0.75rem', background: 'var(--bg-input)', borderRadius: '0.5rem', fontSize: '0.82rem', cursor: 'pointer', border: '1px solid var(--border)' }}
                onClick={() => { setText(h.original); setResult(h.result); }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-muted)', fontSize: '0.72rem', marginBottom: 4 }}>
                  <span>{h.type} / {h.tone || '-'}</span>
                  <span>{new Date(h.created_at).toLocaleDateString()}</span>
                </div>
                <div style={{ color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {h.original?.substring(0, 80)}...
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
